import type { AppProps } from "next/app";
import Layout from "../components/Layout";
import React, { useEffect, useMemo, useRef, useState } from "react";
import SSRProvider from "react-bootstrap/SSRProvider";
import { GrowthBook, GrowthBookProvider } from "@growthbook/growthbook-react";
import { Router } from "next/router";
import Loader from "../components/Loader";
import "bootstrap/dist/css/bootstrap.css";
import "../styles/globals.scss";
import EnquireModalWrapper from "../context/EnquireModalContext";
import OnboardingProvider from "../context/OnboardingContext";
import { WizardOptionsProvider } from "../context/WizardOptionsContext";
import { fetchWizardOptions } from "../utils/wizardOptionsApi";
import {
  FALLBACK_CITIES,
  FALLBACK_COUNTRIES,
  FALLBACK_STUDY_AREAS,
} from "../constants/onboardingFallbacks";
import { PROGRAM_LEVEL_OPTIONS_UNIFIED } from "../utils/b2c/programLevelConstants";
import type { WizardOptions } from "../constants/onboardingFallbacks";
import WizardModal from "../components/B2C/OnboardingWizard/WizardModal";
import dynamic from "next/dynamic";
import { initConsoleOverride } from "../utils/consoleOverride";
// import { useUILoadMetrics } from "../hooks/useUILoadMetrics";
import Script from "next/script";

const InteraktVisibilityControl = dynamic(() => import("../components/WhatsappWidget"), {
  ssr: false,
});

const ExternalLinks = dynamic(() => import("../components/ExternalLinks"), {
  ssr: false,
});

/** Layout props injected in getInitialProps — cached on the client so tab/route switches do not re-fetch CMS and block routeChangeComplete (which left the global Loader stuck). */
type LayoutInjectedProps = {
  footerData: unknown;
  headerData: unknown;
  stickyFooterPages: unknown;
  wizardOptions: WizardOptions | null;
  b2cWizardConfig: unknown;
};

let clientLayoutCache: LayoutInjectedProps | null = null;

function readLayoutFromNextData(): LayoutInjectedProps | null {
  if (typeof window === "undefined") return null;
  const raw = (window as unknown as { __NEXT_DATA__?: { props?: { pageProps?: Record<string, unknown> } } })
    .__NEXT_DATA__?.props?.pageProps;
  if (!raw?.footerData || !raw?.headerData) return null;
  return {
    footerData: raw.footerData,
    headerData: raw.headerData,
    stickyFooterPages: raw.stickyFooterPages,
    wizardOptions: (raw.wizardOptions as WizardOptions | null) ?? null,
    b2cWizardConfig: raw.b2cWizardConfig,
  };
}

function MyApp({ Component, pageProps }: AppProps & { pageProps: any }) {
  const [loading, setLoading] = useState(false);
  const routeLoadsRef = useRef(0);

  const growthBook = useMemo(
    () =>
      new GrowthBook({
        apiHost: process.env.NEXT_PUBLIC_GROWTHBOOK_URL,
        clientKey: process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_ID,
        enableDevMode: process.env.NODE_ENV !== "production",
        subscribeToChanges: true,
      }),
    [],
  );

  useEffect(() => {
    growthBook.init({ streaming: true });
  }, [growthBook]);

   useEffect(() => {
    if (typeof window !== 'undefined') {
      initConsoleOverride();
    }
  }, []);


  useEffect(() => {
    const start = () => {
      routeLoadsRef.current += 1;
      setLoading(true);
    };
    const end = () => {
      routeLoadsRef.current = Math.max(0, routeLoadsRef.current - 1);
      if (routeLoadsRef.current === 0) {
        setLoading(false);
      }
    };
    Router.events.on("routeChangeStart", start);
    Router.events.on("routeChangeComplete", end);
    Router.events.on("routeChangeError", end);
    return () => {
      Router.events.off("routeChangeStart", start);
      Router.events.off("routeChangeComplete", end);
      Router.events.off("routeChangeError", end);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const p = pageProps as Record<string, unknown>;
    if (p.footerData && p.headerData) {
      clientLayoutCache = {
        footerData: p.footerData,
        headerData: p.headerData,
        stickyFooterPages: p.stickyFooterPages,
        wizardOptions: (p.wizardOptions as WizardOptions | null) ?? null,
        b2cWizardConfig: p.b2cWizardConfig,
      };
    }
  }, [pageProps]);

  useEffect(() => {
    if (!loading || typeof window === "undefined") return;
    const id = window.setTimeout(() => {
      routeLoadsRef.current = 0;
      setLoading(false);
    }, 12000);
    return () => window.clearTimeout(id);
  }, [loading]);

  useEffect(() => {
    function addLink(rel: string, href: string) {
      const link = document.createElement("link");
      link.rel = rel;
      link.href = href;
      document.head.appendChild(link);
    }

    addLink("preconnect", process.env.NEXT_PUBLIC_API_BASE_URL ?? "");
    addLink("dns-prefetch", process.env.NEXT_PUBLIC_API_BASE_URL ?? "");
  }, []);

  return (
    <SSRProvider>
      {loading && <Loader />}
      <Script
        id="openai-pixel-script"
        strategy="afterInteractive"
      >
        {`!function(w,d,s,u){if(w.oaiq)return;var q=function(){q.q.push(arguments)};q.q=[];w.oaiq=q;var j=d.createElement(s);j.async=1;j.src=u;var f=d.getElementsByTagName(s)[0];f.parentNode.insertBefore(j,f)}(window,document,"script","https://bzrcdn.openai.com/sdk/oaiq.min.js");oaiq("init",{pixelId:"9ykBv3Cd8hHLPyM5CQ3V2i"});`}
      </Script>
      <Script
        id="poptin-single-page-app"
        strategy="afterInteractive"
      >
        {`var poptin_single_page_app = true;`}
      </Script>
      <Script
        src="https://cdn.popt.in/pixel.js?id=7376c7a3164b8"
        id="pixel-script-poptin"
        strategy="lazyOnload"
      />
      <Script
        id="whatsapp-integration-script"
        strategy="afterInteractive"
      >
        {`(function(w,d,s,c,r,a,m){
          w['KiwiObject']=r;
          w[r]=w[r] || function () {
            (w[r].q=w[r].q||[]).push(arguments)};
          w[r].l=1*new Date();
            a=d.createElement(s);
            m=d.getElementsByTagName(s)[0];
          a.async=1;
          a.src=c;
          m.parentNode.insertBefore(a,m)
        })(window,document,'script',"https://app.interakt.ai/kiwi-sdk/kiwi-sdk-17-prod-min.js?v="+ new Date().getTime(),'kiwi');
            window.addEventListener("load",function () {
            kiwi.hidePoweredBy();
            kiwi.init('', 'IHldDVmNFrlknyH5oXyvAlrt3IEN0xcY', {});
        });`}
      </Script>
      <InteraktVisibilityControl />
      <GrowthBookProvider growthbook={growthBook}>
        <WizardOptionsProvider value={pageProps.wizardOptions || { countries: FALLBACK_COUNTRIES, degreeLevels: PROGRAM_LEVEL_OPTIONS_UNIFIED, studyAreas: FALLBACK_STUDY_AREAS, cities: FALLBACK_CITIES, nearestOffices: [], coachingOptions: [] }}>
          <OnboardingProvider>
            <EnquireModalWrapper b2cWizardConfig={pageProps.b2cWizardConfig}>
              <Layout {...pageProps}>
                <Component {...pageProps} />
              </Layout>
            </EnquireModalWrapper>
            <WizardModal />
          </OnboardingProvider>
        </WizardOptionsProvider>
      </GrowthBookProvider>
      <ExternalLinks />
    </SSRProvider>
  );
}


export default MyApp;
